import { IgDivider } from "@intelligems/ig-theme";
import type {
  ComponentPlacementLocation,
  OnsiteUpsellRuleAuthoring,
  UpsellCondition,
} from "@intelligems/ig-types";
import { Panel } from "@src/features/experienceBuilder/modifications/common/panel/Panel";
import { ModificationHeader } from "@src/features/experienceBuilder/modifications/common/ModificationHeader";
import { AgenticLogicSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/AgenticLogicSection";
import { DiscountSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/DiscountSection";
import { DormantAnchorPlacementSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/DormantAnchorPlacementSection";
import { GeneralSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/GeneralSection";
import { LegacyPlacementSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/LegacyPlacementSection";
import { PlacementSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/PlacementSection";
import { RulesSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/RulesSection";
import { TemplateSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/TemplateSection";
import { useDispatch, useSelector } from "@src/store";
import {
  ExperienceBuilderAction as Action,
  ExperienceBuilderSelector as Selector,
} from "@src/store/slices/experienceBuilder";
import {
  DEFAULT_PLACEMENT,
  placementForMode,
  placementMode,
} from "@src/store/slices/experienceBuilder/component/utils/onsiteUpsellModHandler";
import type {
  OnsiteUpsellComponent,
  PlacementMode,
} from "@src/store/slices/experienceBuilder/types";
import { useIsAgenticLogicEnabled } from "@src/hooks/feature-flags/useIsAgenticLogicEnabled";
import { assert } from "@src/utils/assert";

// Conditions the schema forbids on each location (`refineOnsiteUpsellRules`), so
// switching location prunes anything no longer valid rather than persisting an
// error the merchant can't see the source of.
const INVALID_ON: Record<ComponentPlacementLocation, Set<string>> = {
  pdp: new Set(["variants", "cartSubtotal", "cartItemCount"]),
  cart: new Set(["products", "productPrice"]),
};

export const OnsiteUpsellComponentPanel = () => {
  const dispatch = useDispatch();
  // Mirrors the backend gate exactly (ConfigProvider.isAgenticLogicEnabled).
  // An org that is not on the engine has no rules to bind to.
  const agenticLogicEnabled = useIsAgenticLogicEnabled();

  const mod = useSelector(Selector.component.getVariationDraft) as
    | OnsiteUpsellComponent
    | undefined;
  assert(mod !== undefined, "mod is undefined");
  const isApplicable = useSelector(Selector.isVariationDraftApplicable);
  const errors = useSelector(Selector.getVariationDraftUIErrors);

  // Engine orgs must pick a rule — the legacy editor is never offered to them.
  // The gate lives here rather than in the mod handler because the flag comes
  // from react-query and `getErrors` is pure.
  const ruleMissing = agenticLogicEnabled && mod.agenticLogicRuleId == null;

  const update = (changes: Partial<OnsiteUpsellComponent>) =>
    dispatch(Action.component.setDraft({ ...mod, ...changes }));
  const cancel = () => void dispatch(Action.cancelDraft({ trackCancel: true }));
  const apply = () => void dispatch(Action.applyDraft());

  const changeLocation = (location: ComponentPlacementLocation) => {
    if (location === mod.location) {
      return;
    }
    const invalid = INVALID_ON[location];
    const rules: OnsiteUpsellRuleAuthoring[] = mod.rules.map((rule) => ({
      ...rule,
      conditions: rule.conditions.filter(
        (c: UpsellCondition) => !invalid.has(c.type)
      ),
    }));
    update({
      location,
      rules,
      // An anchored placement keeps its key across a location change — the
      // merchant's theme block didn't move. Only the selector defaults, which
      // are per-location, need resetting.
      ...(mod.anchorKey
        ? {}
        : {
            selector: DEFAULT_PLACEMENT[location].selector,
            insertType: DEFAULT_PLACEMENT[location].insertType,
          }),
    });
  };

  const changeMode = (mode: PlacementMode) => {
    if (mode === placementMode(mod)) {
      return;
    }
    update(placementForMode(mode, mod.location, mod));
  };

  // Three placement editors, one per org/component state:
  //   engine org            -> the full editor, theme-block or selector
  //   legacy org, anchored  -> read-only, because only the engine writes anchors
  //   legacy org, selector  -> the legacy editor
  const renderPlacementSection = () => {
    if (agenticLogicEnabled) {
      return (
        <PlacementSection
          location={mod.location}
          mode={placementMode(mod)}
          anchorKey={mod.anchorKey ?? ""}
          selector={mod.selector ?? ""}
          insertType={mod.insertType}
          anchorKeyError={Boolean(errors?.anchorKey)}
          selectorError={Boolean(errors?.selector)}
          onLocationChange={changeLocation}
          onModeChange={changeMode}
          onChange={update}
        />
      );
    }

    if (mod.anchorKey) {
      return (
        <DormantAnchorPlacementSection
          location={mod.location}
          anchorKey={mod.anchorKey}
        />
      );
    }

    return (
      <LegacyPlacementSection
        location={mod.location}
        selector={mod.selector ?? DEFAULT_PLACEMENT[mod.location].selector}
        insertType={
          mod.insertType ?? DEFAULT_PLACEMENT[mod.location].insertType
        }
        selectorError={Boolean(errors?.selector)}
        onLocationChange={changeLocation}
        onChange={update}
      />
    );
  };

  return (
    <>
      <ModificationHeader />
      <Panel>
        <Panel.Body>
          {renderPlacementSection()}

          <IgDivider />

          {!agenticLogicEnabled && (
            <>
              <GeneralSection
                maxProducts={mod.maxProducts}
                excludeOutOfStock={mod.excludeOutOfStock}
                excludeItemsInCart={mod.excludeItemsInCart}
                onChange={update}
              />

              <IgDivider />
            </>
          )}

          <DiscountSection
            discount={mod.discount}
            valueError={Boolean(errors?.["discount.discountValue"])}
            onChange={(discount) => update({ discount })}
          />

          <IgDivider />

          <Panel.Header>Rules</Panel.Header>
          {/* One editor per org, split on the engine flag: engine orgs select
              an Agentic Logic rule (required — the Apply gate below), legacy
              orgs keep the legacy rules editor. Never both. */}
          {agenticLogicEnabled ? (
            <AgenticLogicSection mod={mod} onChange={update} />
          ) : (
            <RulesSection
              rules={mod.rules}
              location={mod.location}
              onChange={(rules) => update({ rules })}
            />
          )}

          <IgDivider />

          <TemplateSection template={mod.template} onChange={update} />
        </Panel.Body>

        <Panel.Buttons>
          <Panel.CancelButton onClick={() => void cancel()} />
          <Panel.ApplyButton
            onClick={apply}
            disabled={!isApplicable || ruleMissing}
          />
        </Panel.Buttons>
      </Panel>
    </>
  );
};
